/*
    2 components

    - NotesPopover spawns popover with header that has built in close button and possibility to add extra content into it
    - GroupSelect - component that display certain group options once this group selected

*/

import { Box, IconButton, Popover, PopoverProps, Stack, Typography } from '@mui/material';

type Props = {
  anchorEl: HTMLElement;
  title: string;
  open: boolean;
  icon?: React.ReactNode;
  onOpenChange?: (value: boolean) => void;
  children?: React.ReactNode;
  headerStartContent?: React.ReactNode;
  headerEndContent?: React.ReactNode;
} & PopoverProps;

export function NotesPopover({
  children,
  anchorEl,
  icon,
  onOpenChange,
  open,
  title,
  headerStartContent,
  headerEndContent,
  ...props
}: Props) {
  return (
    <Popover
      {...props}
      open={open}
      onClose={() => onOpenChange(false)}
      anchorEl={anchorEl}
      anchorOrigin={
        props.anchorOrigin || {
          vertical: 'top',
          horizontal: -10,
        }
      }
      transformOrigin={
        props.transformOrigin || {
          vertical: 'top', // Align to the top of the popover
          horizontal: 'right', // Make the popover open towards the left side
        }
      }
      PaperProps={{
        elevation: 0,
        sx: {
          background: 'var(--Gray-50, #F9FAFB)',
          overflow: 'hidden',
          borderRadius: '12px',
          border: `1px solid var(--Gray-300, #D0D5DD)`,
          width: '412px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: `0px 0px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)`,
          transition: 'height 0.3s ease',
          ...props?.PaperProps?.sx,
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: '1px solid var(--Gray-200, #EAECF0)',
          background: '#FFF',
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 0 0' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
              background: 'var(--Gray-100, #F2F4F7)',
              borderRadius: '8px',
              color: '#000',
            }}
          >
            {typeof icon === 'string' && <i className={icon} style={{ fontSize: '18px' }} />}
            {typeof icon !== 'string' && icon}
          </Box>
          <Typography
            sx={{
              overflow: 'hidden',
              color: 'var(--Black, #000)',
              textOverflow: 'ellipsis',
              fontFamily: 'Inter',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '24px', // or '150%' if you'd prefer that for line height
            }}
          >
            {title}
          </Typography>

          {headerStartContent}
        </Stack>

        <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {headerEndContent}

          <IconButton
            sx={{
              width: '36px',
              height: '36px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#101828',
            }}
            onClick={() => onOpenChange(false)}
          >
            <i className="fa-light fa-xmark" style={{ fontSize: '24px' }} />
          </IconButton>
        </Stack>
      </Stack>

      {children}
    </Popover>
  );
}
